import React, { useState } from 'react'

import { SxProps } from '@mui/material'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import FormSection from 'components_new/molecules/FormSection'
import FormToggle from 'components_new/molecules/FormToggle'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import ConfigSection from './ConfigSection'
import FilterSection from './FilterSection'
import Permissions from './permissions'
import Loading from './loading'

import { useMetric } from 'redux/hooks/Metrics'
import { useParams } from 'utils/hooks'

import { useMetricActions } from 'redux/actions/Metrics'
import {
  IMetric,
  MetricStatus,
  TMetricWithConfig,
  isCustomEconomyMetric
} from 'types/GlobalMetric'
import { useTranslation } from 'language/translator'

interface MetricSettingsProps {
  sx?: SxProps
}

const allowConfig = (metric: IMetric): metric is TMetricWithConfig =>
  !!metric.config

const MetricSettings = (props: MetricSettingsProps) => {
  const { sx } = props
  const { id } = useParams<{ id: string }>()
  const { lang, t } = useTranslation()

  const { tryUpdateMetricSettings } = useMetricActions()

  const metric = useMetric(id, null)

  const [activateDialog, setActivateDialog] = useState<boolean>(false)

  if (!metric) {
    return <Loading />
  }

  const active = metric.status === MetricStatus.ACTIVE
  const missingData = metric.status === MetricStatus.MISSING_DATA
  const missingSlices = metric.status === MetricStatus.MISSING_SLICES
  const incompleteConfig = metric.config ? metric.config.incomplete : false
  const isCustomEconomy = isCustomEconomyMetric(metric)

  const getAvailabilityTooltip = (): string | undefined => {
    if (isCustomEconomy) {
      return t('metrics', 'custom_economy_availability_tooltip')
    }

    if (missingSlices) {
      return `${t(
        'metrics',
        'missing_slices_tooltip'
      )}: ${metric.definition_slices
        .filter((slice) => !slice.configured)
        .map((slice) => slice.name)
        .join(', ')}`
    }

    if (incompleteConfig) {
      return t('global', 'missing_configuration')
    }

    return undefined
  }

  return (
    <>
      <Box sx={sx}>
        <FormSection
          helperText={t('metrics', 'availability_description')}
          importance="optional"
          title={t('metrics', 'availability_title')}
        >
          <Tooltip title={getAvailabilityTooltip()}>
            <span>
              <FormToggle
                body={t('metrics', 'visible_to_users')}
                disabled={
                  missingData ||
                  missingSlices ||
                  incompleteConfig ||
                  isCustomEconomy
                }
                icon={{ name: 'VisibilityOutlined' }}
                onChange={() => setActivateDialog(true)}
                title={t('metrics', 'activated_metric')}
                value={active}
              />
            </span>
          </Tooltip>
        </FormSection>
        <Permissions edit={!missingData} />
        <FilterSection edit={!missingData} metricId={metric.id} />
        {allowConfig(metric) ? <ConfigSection metric={metric} /> : null}
      </Box>

      <SimpleDialog
        actions={
          <>
            <Button onClick={() => setActivateDialog(false)} variant="text">
              {t('global', 'cancel')}
            </Button>
            <Button
              onClick={() => {
                tryUpdateMetricSettings(id, { active: !active })
                setActivateDialog(false)
              }}
            >
              {t('global', active ? 'deactivate' : 'activate')}
            </Button>
          </>
        }
        onClose={() => setActivateDialog(false)}
        open={activateDialog}
        title={t('metrics', active ? 'deactivate_metric' : 'activate_metric')}
      >
        <Text>
          Gör <code>{metric.title[lang]}</code>{' '}
          {active ? 'otillgängligt' : 'tillgängligt'} för organisationen.
          {active ? (
            <ul className="ul">
              <li className="li">
                {t('metrics', 'inactive_metric_description')}
              </li>
              <li className="li">{t('metrics', 'inactive_widgets_warning')}</li>
            </ul>
          ) : (
            <ul className="ul">
              <li className="li">
                {t('metrics', 'active_metric_description')}
              </li>
              <li className="li">{t('metrics', 'visible_to_users')}</li>
            </ul>
          )}
          Är du säker?
        </Text>
      </SimpleDialog>
    </>
  )
}

export default MetricSettings
